import React, { useMemo, useState } from 'react';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { LoadingSpinner, StandardPageLayout, Button, Modal, FormInput, FormSelect, Avatar } from '../components/Common.jsx';
import { PlusCircle } from 'lucide-react';
import { useNotification } from '../context/NotificationContext.jsx';
import { Box, TextField, InputAdornment, MenuItem, Select, FormControl, InputLabel, Stack } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter.js';

const PartnerForm = ({ onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        adminEmail: '',
        domain: '',
        status: 'Active'
    });
    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <FormInput label="Partner Name" name="name" value={formData.name} onChange={handleChange} required />
            <FormInput label="Contact Email" name="email" type="email" value={formData.email} onChange={handleChange} required />
            <FormInput label="Admin Email" name="adminEmail" type="email" value={formData.adminEmail} onChange={handleChange} required />
            <FormInput label="Domain" name="domain" value={formData.domain} onChange={handleChange} />
            <FormSelect label="Status" name="status" value={formData.status} onChange={handleChange}>
                <option>Active</option>
                <option>Inactive</option>
            </FormSelect>
            <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="secondary" onClick={onCancel} withShadow>Cancel</Button>
                <Button type="submit">Create Partner</Button>
            </div>
        </form>
    );
};

const PartnersPage = ({ token, onSelectPartner }) => {
    const [statusFilter, setStatusFilter] = useState('All');
    const includeDeleted = statusFilter === 'Deleted' || statusFilter === 'All';
    const partnersEndpoint = includeDeleted ? '/partners?include_deleted=true' : '/partners';
    const { data: partners, isLoading, error, refresh } = useData(partnersEndpoint, token);
    const { formatDate } = useTimezoneFormatter();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { addNotification } = useNotification();

    const handleSave = async (partnerData) => {
        try {
            await apiFetch('/partners', token, { method: 'POST', body: JSON.stringify(partnerData) });
            addNotification(`Partner "${partnerData.name}" created successfully.`);
            setIsModalOpen(false);
            refresh();
        } catch (err) {
            addNotification("Error creating partner: " + err.message, 'error');
        }
    };

    // Filter Logic
    const [searchTerm, setSearchTerm] = useState('');

    const handleRestore = async (id) => {
        try {
            await apiFetch('/admin/trash/restore', token, { method: 'POST', body: JSON.stringify({ table: 'partners', record_id: id }) });
            addNotification('Partner restored successfully.', 'success');
            refresh();
        } catch (err) { addNotification('Restore failed: ' + err.message, 'error'); }
    };
    const [sortOption, setSortOption] = useState('updated_desc');

    const filteredPartners = (partners || []).filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.domain?.toLowerCase().includes(searchTerm.toLowerCase());
        if (statusFilter === 'Deleted') return matchesSearch && p.is_deleted;
        const matchesStatus = statusFilter === 'All' || p.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const sortedPartners = useMemo(() => {
        const list = [...filteredPartners];
        list.sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.updated_at || a.joinDate || 0).getTime();
            const dateB = new Date(b.updatedAt || b.updated_at || b.joinDate || 0).getTime();
            if (sortOption === 'updated_desc') return dateB - dateA;
            if (sortOption === 'updated_asc') return dateA - dateB;
            if (sortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
            if (sortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
            return 0;
        });
        return list;
    }, [filteredPartners, sortOption]);

    const filterControls = (
        <Stack direction="row" spacing={2} alignItems="center">
            <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                    ),
                }}
                sx={{ width: 200, bgcolor: 'background.paper' }}
            />
            <FormControl size="small" sx={{ minWidth: 100, bgcolor: 'background.paper' }}>
                <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    displayEmpty
                >
                    <MenuItem value="All">All Status</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                    <MenuItem value="Deleted">Deleted</MenuItem>
                </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170, bgcolor: 'background.paper' }}>
                <Select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    displayEmpty
                >
                    <MenuItem value="updated_desc">Recently Updated</MenuItem>
                    <MenuItem value="updated_asc">Oldest Updated</MenuItem>
                    <MenuItem value="name_asc">Name A-Z</MenuItem>
                    <MenuItem value="name_desc">Name Z-A</MenuItem>
                </Select>
            </FormControl>
        </Stack>
    );

    if (isLoading) return <LoadingSpinner />;
    if (error) return <div className="p-4 text-red-700 bg-red-100 rounded-md">Error loading partners: {error.message}</div>;

    const headerActions = (
        <Button onClick={() => setIsModalOpen(true)} sx={{ height: 32 }}>
            <PlusCircle className="mr-2 h-4 w-4" />ADD PARTNER
        </Button>
    );

    return (
        <StandardPageLayout title="Partners" description="Manage partner organizations and their details." headerActions={headerActions} filterControls={filterControls}>
            <Box className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Partner</th>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Tenants</th>
                            <th className="p-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Join Date</th>
                            <th className="p-4 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedPartners.map(p => (
                            <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => onSelectPartner(p.id)}>
                                <td className="p-4">
                                    <div className="flex items-center">
                                        <Avatar src={p.logo} name={p.name} size={40} />
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">{p.name}</div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">{p.domain}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4">
                                    {p.is_deleted
                                        ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-200 text-gray-600">Deleted</span>
                                        : <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${p.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{p.status}</span>}
                                </td>
                                <td className="p-4 text-sm text-gray-500 dark:text-gray-300">{p.tenantCount}</td>
                                <td className="p-4 text-sm text-gray-500 dark:text-gray-300">{formatDate(p.joinDate)}</td>
                                <td className="p-4 text-center">
                                    {p.is_deleted
                                        ? <Button variant="secondary" onClick={(e) => { e.stopPropagation(); handleRestore(p.id); }} sx={{ boxShadow: 2, bgcolor: 'background.paper' }}>Restore</Button>
                                        : <Button variant="secondary" sx={{ boxShadow: 2, bgcolor: 'background.paper' }}>Manage</Button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Add New Partner">
                <PartnerForm onSave={handleSave} onCancel={() => setIsModalOpen(false)} />
            </Modal>
        </StandardPageLayout>
    );
};

export default PartnersPage;